"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.maxMonthYearNullable = exports.isProjectDateRangeFullyLocked = exports.formatLockBoundary = exports.validateNotBeforeLockBoundary = exports.isBeforeOrOnLockBoundary = exports.getFirstAllowableCapitalizationStartMonthYear = exports.isCapitalizationEndDateLocked = exports.isCapitalizationStartDateLocked = exports.isCapitalizationStatusLocked = void 0;
const date_utils_1 = require("../date.utils");
const month_year_utils_1 = require("../month-year.utils");
// Regex for YYYY-MM format
const LOCK_BOUNDARY_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * Parses and validates a lock boundary string.
 * @param lockBoundary - The lock boundary in YYYY-MM format
 * @returns The parsed MonthYear
 * @throws Error if the lock boundary is not in valid YYYY-MM format
 */
const parseLockBoundary = (lockBoundary) => {
    if (!LOCK_BOUNDARY_REGEX.test(lockBoundary)) {
        throw new Error(`Invalid lock boundary format: "${lockBoundary}". Expected YYYY-MM format (e.g., "2024-06").`);
    }
    return (0, month_year_utils_1.stringToMonthYear)(lockBoundary);
};
/**
 * Determines if capitalization status is locked based on lock boundary.
 * Status is locked when a capitalization start date exists and is on or before the lock boundary.
 *
 * @param capitalizationStartDate - The project's capitalization start date (Date, string, or null)
 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
 * @returns true if status is locked, false otherwise
 */
const isCapitalizationStatusLocked = (capitalizationStartDate, lockBoundary) => {
    if (!lockBoundary) {
        return false;
    }
    if (!capitalizationStartDate) {
        return false;
    }
    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
    const startDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(capitalizationStartDate);
    // Locked if start date is on or before lock boundary
    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(startDateMonthYear, lockBoundaryMonthYear);
};
exports.isCapitalizationStatusLocked = isCapitalizationStatusLocked;
/**
 * Determines if capitalization start date is locked based on lock boundary.
 * Same logic as status - both are locked together.
 */
const isCapitalizationStartDateLocked = (capitalizationStartDate, lockBoundary) => (0, exports.isCapitalizationStatusLocked)(capitalizationStartDate, lockBoundary);
exports.isCapitalizationStartDateLocked = isCapitalizationStartDateLocked;
/**
 * Determines if capitalization end date is locked based on lock boundary.
 * End date is more permissive - it can be modified if it's on or after the lock boundary.
 *
 * @param capitalizationEndDate - The project's capitalization end date (Date, string, or null)
 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
 * @returns true if end date is locked, false otherwise
 */
const isCapitalizationEndDateLocked = (capitalizationEndDate, lockBoundary) => {
    if (!lockBoundary) {
        return false;
    }
    if (!capitalizationEndDate) {
        return false;
    }
    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
    const endDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(capitalizationEndDate);
    // Locked only if end date is strictly before lock boundary.
    // If it's on the boundary, it can still be modified.
    return (0, month_year_utils_1.isMonthYearBefore)(endDateMonthYear, lockBoundaryMonthYear);
};
exports.isCapitalizationEndDateLocked = isCapitalizationEndDateLocked;
/**
 * Gets the first allowable month/year for capitalization start date.
 * Returns the month AFTER the lock boundary, or null if no lock boundary.
 */
const getFirstAllowableCapitalizationStartMonthYear = (lockBoundary) => {
    if (!lockBoundary) {
        return null;
    }
    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
    return (0, date_utils_1.getNextMonthYear)(lockBoundaryMonthYear);
};
exports.getFirstAllowableCapitalizationStartMonthYear = getFirstAllowableCapitalizationStartMonthYear;
/**
 * Checks if a MonthYear value is before or on the lock boundary.
 * Used for form validation.
 */
const isBeforeOrOnLockBoundary = (value, lockBoundary) => {
    if (!value || !lockBoundary) {
        return false;
    }
    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(value, lockBoundaryMonthYear);
};
exports.isBeforeOrOnLockBoundary = isBeforeOrOnLockBoundary;
/**
 * Validates a MonthYear value against the lock boundary and returns an error message if invalid.
 * Returns null if the value is valid (after the lock boundary or no lock boundary set).
 * Used for form validation to DRY up error message generation.
 */
const validateNotBeforeLockBoundary = (value, lockBoundary) => {
    if ((0, exports.isBeforeOrOnLockBoundary)(value, lockBoundary)) {
        return `Cannot set date before locked period (${(0, exports.formatLockBoundary)(lockBoundary)})`;
    }
    return null;
};
exports.validateNotBeforeLockBoundary = validateNotBeforeLockBoundary;
/**
 * Formats a lock boundary string for display.
 * @example "2024-06" → "Jun 2024"
 */
const formatLockBoundary = (lockBoundary) => {
    if (!lockBoundary) {
        return '';
    }
    const monthYear = parseLockBoundary(lockBoundary);
    return `${(0, date_utils_1.getMonthLongAbbreviation)(monthYear.month)} ${monthYear.year}`;
};
exports.formatLockBoundary = formatLockBoundary;
/**
 * Checks if a project's date range falls entirely within the locked period.
 * This means no valid capitalization dates can be set (all dates within project range are locked).
 *
 * @param projectEndDate - The project's end date (Date, string, or null)
 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
 * @returns true if the entire project date range is locked, false otherwise
 */
const isProjectDateRangeFullyLocked = (projectEndDate, lockBoundary) => {
    if (!lockBoundary || !projectEndDate) {
        return false;
    }
    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
    const endDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(projectEndDate);
    // If project ends on or before the lock boundary, all dates are locked
    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(endDateMonthYear, lockBoundaryMonthYear);
};
exports.isProjectDateRangeFullyLocked = isProjectDateRangeFullyLocked;
/**
 * Returns the maximum of two MonthYear values, or the one that's not null.
 * Used for determining the best date when toggling capitalization on.
 */
const maxMonthYearNullable = (a, b) => {
    if (!a)
        return b;
    if (!b)
        return a;
    const dateA = (0, month_year_utils_1.getFirstDateOfMonthYear)(a);
    const dateB = (0, month_year_utils_1.getFirstDateOfMonthYear)(b);
    return dateA > dateB ? a : b;
};
exports.maxMonthYearNullable = maxMonthYearNullable;
